import { useCallback, useEffect, useRef } from 'react';
import { getSettings } from '../services/api';

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND = '/notification.mp3';

export const useNotifications = () => {
  const settingsRef = useRef({ push_notifications: true, sound_alerts: true });
  const audioRef = useRef(null);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getSettings();
        settingsRef.current = settings.notifications || { push_notifications: true, sound_alerts: true };
      } catch (e) {
        console.error('Failed to load notification settings:', e);
      }
    };
    loadSettings();

    // Create audio element for notification sound
    audioRef.current = new Audio();
    audioRef.current.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYZITCmSAAAAAAD/+1DEAAAGAAGn9AAAIgAANP8AAABMQUXLA5JJJGAYBh/EHPwQc/BA5/+XB3d3WODu7u7u7u7u/lwd3d3d3d3d3cHd3d3d3d3d/u7u7u7u7u7u7u7u7u7u7v/u7u7u7v/8uBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/7UMQPg8AAAaQAAAAgAAA0gAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQxCQDwAABpAAAACAAADSAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }, []);

  // Play notification sound
  const playSound = useCallback(() => {
    if (settingsRef.current.sound_alerts && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Could not play sound:', e));
    }
  }, []);

  // Show browser notification
  const showNotification = useCallback(async (title, options = {}) => {
    if (!settingsRef.current.push_notifications) {
      return;
    }

    const hasPermission = await requestPermission();
    if (!hasPermission) {
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: options.tag || 'default',
        renotify: true,
        ...options
      });

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);

      // Play sound
      playSound();

      return notification;
    } catch (e) {
      console.error('Failed to show notification:', e);
    }
  }, [requestPermission, playSound]);

  // Notify about new message
  const notifyNewMessage = useCallback((customerName, message, channel) => {
    const channelNames = {
      email: 'Email',
      whatsapp: 'WhatsApp',
      facebook: 'Facebook Messenger'
    };
    
    showNotification(`New message from ${customerName}`, {
      body: message?.substring(0, 100) || 'New message received',
      tag: `message-${Date.now()}`,
      data: { channel }
    });
  }, [showNotification]);

  // Notify about new conversation
  const notifyNewConversation = useCallback((customerName, channel) => {
    showNotification(`New conversation started`, {
      body: `${customerName} started a new ${channel} conversation`,
      tag: `conversation-${Date.now()}`
    });
  }, [showNotification]);

  return {
    requestPermission,
    showNotification,
    notifyNewMessage,
    notifyNewConversation,
    playSound
  };
};

export default useNotifications;
